import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ListCard from "@/components/cards/ListCard";
import { UserDetailsSkeleton } from "@/components/skeletons";
import UserActivationModal from "@/components/modals/UserActivationModal";
import { getUserById, getUserEnvelopes, suspendUser, unsuspendUser, updateUser } from "@/services/user.service";
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";
import { concatenateName, DateFormatter, TimeFormatter } from "@/utils/functions/helperFunctions";
import SuspendUserModal from "@/components/modals/SuspendUserModal";
import { isFeaturePermitted } from "@/utils/functions/rbac";
import { SUCCESS_MESSAGES } from "@/utils/constants/appConstants/commonConstants";
import { BUTTONS, USER_STATUS } from "@/utils/constants/appConstants/commonConstants";
import { errorToasterMessage, successToasterMessage } from "@/utils/functions/toaster";
import AppButton from "@/components/buttons/AppButton";

const UserDetails = () => {
    const [userDetails, setUserDetails] = useState<any>({});
    const [userOrganization, setUserOrganization] = useState<any>({});
    const [userEnvelopes, setUserEnvelopes] = useState("");
    const [showActivationModal, setShowActivationModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showSuspendModal, setShowSuspendModal] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);
    const [isAdminPortalSuspentionPermitted, setIsAdminPortalSuspentionPermitted] = useState(false);
    const params = useParams();
    const id = params.id || "";
    const [refetch, setRefetch] = useState(false);

    const refetchHandler = () => {
        setRefetch(!refetch);
    }

    useEffect(() => {
        setIsAdminPortalSuspentionPermitted(isFeaturePermitted("AdminPortalSuspendUser"));
    }, []);
    useEffect(() => {
        setIsLoading(true);
        getUserById(
            id,
            (response: any) => {
                setIsLoading(false);
                getUserEnvelopes(
                    id,
                    (res: any) => {
                        setUserEnvelopes(res);
                    },
                    (err: any) => {
                        console.log(err);
                    }
                );
                setUserDetails(response.data);
                const currentOrganization = getCurrentOrganization();
                const userOrganizationList = response.data?.organizations;
                const userCurrentOrganization = userOrganizationList.find((obj: any) => obj.organization === currentOrganization.organization);
                setUserOrganization(userCurrentOrganization);
            },
            (error: any) => {
                setIsLoading(false);
                console.log(error);
            }
        );
    }, [refetch]);

    const changeUserStatus = () => {
        setStatusLoading(true);
        updateUser(
            id,
            {
                is_active: !userOrganization?.is_active,
            },
            (response: any) => {
                setStatusLoading(false);
                const currentOrganization = getCurrentOrganization();
                const userOrganizationList = response.data?.organizations;
                const userCurrentOrganization = userOrganizationList.find((obj: any) => obj.organization === currentOrganization.organization);
                setUserOrganization(userCurrentOrganization);
                setShowActivationModal(false);
            },
            (err: any) => {
                setStatusLoading(false);
                setShowActivationModal(false);
                console.log(err);
            }
        );
    };

    const suspendUserhandler = async (reason: string) => {
        setStatusLoading(true);
        await suspendUser(
            { user_id: id, reason: reason },
            (response: any) => {
                successToasterMessage(SUCCESS_MESSAGES.USER_SUSPENDED);
                setStatusLoading(false);
                setShowSuspendModal(false);
                refetchHandler();
            },
            (err: any) => {
                errorToasterMessage(err.response.data.message);
                setStatusLoading(false);
                setShowSuspendModal(false);
            }
        );
    }

    const handleUnsuspendUser = async () => {
        setStatusLoading(true);
        await unsuspendUser(
            { user_id: id },
            (response: any) => {
                successToasterMessage(SUCCESS_MESSAGES.USER_UNSUSPENDED);
                refetchHandler();
            },
            (err: any) => {
                errorToasterMessage(err.response.data.message);
            }
        );
        setStatusLoading(false);
    }

    const handleSuspendStatusUser = () => {
        if (userDetails.status !== USER_STATUS.SUSPENDED) {
            setShowSuspendModal(true);
        } else {
            handleUnsuspendUser();
        }
    }

    return isLoading ? (
        <UserDetailsSkeleton />
    ) : (
        <>
            <div className="gs-detail-header">
                <h3 className="gs-detail-title" id='user-detail-email-address'>{userDetails.email_address}</h3>
                {isAdminPortalSuspentionPermitted && (
                    <AppButton
                        btnText={userDetails.status === USER_STATUS.SUSPENDED ? BUTTONS.UNSUSPEND : BUTTONS.SUSPEND}
                        isLoading={statusLoading}
                        className="btn-danger btn-icon btn-icon-light btn-sm p-1"
                        onClick={handleSuspendStatusUser}
                        disabled={statusLoading || !(userDetails.is_active || userDetails.status === USER_STATUS.SUSPENDED)}
                    />
                )}
            </div>
            <div className="gs-list-page-card">
                <ul className="list-none m-0 p-0">
                    <ListCard id='user-id' label="User ID" value={userDetails.id} />
                    <ListCard
                        id='user-status'
                        label="Status"
                        value={
                            <span className="text-primary hover:underline cursor-pointer" id='user-status' onClick={() => setShowActivationModal(true)}>
                                {userDetails.status === USER_STATUS.SUSPENDED ? USER_STATUS.SUSPENDED.toUpperCase() : userOrganization?.is_active === true ? USER_STATUS.ACTIVE.toUpperCase() : USER_STATUS.INACTIVE.toUpperCase()}
                            </span>
                        }
                    />
                    <ListCard id='user-full-name' label="Full Name" value={concatenateName(userDetails.first_name, userDetails.last_name)} />
                    <ListCard id='user-envelope-owned' label="Envelopes Owned" value={userEnvelopes?.length} />
                    <ListCard id='user-roles' label="Roles" value={<Link className="text-primary hover:underline" to={`/user/${userDetails?.id}/roles`} id='user-roles-count'>{userOrganization?.roles?.length ? userOrganization.roles.length : 0}</Link>} />
                    <ListCard id='user-groups' label="Groups" value={<Link className="text-primary hover:underline" to={`/user/${userDetails?.id}/groups`} id='user-groups-count'>{userOrganization?.groups?.length ? userOrganization.groups.length : 0}</Link>} />
                    <ListCard id='user-created-at' label="Created At" value={`${DateFormatter(userDetails.created_at)} | ${TimeFormatter(userDetails.created_at)}`} />
                </ul>
            </div>
            <SuspendUserModal show={showSuspendModal} onHide={() => setShowSuspendModal(false)} onSuspend={suspendUserhandler} loading={statusLoading} />
            <UserActivationModal show={showActivationModal} onHide={() => setShowActivationModal(false)} changeStatus={changeUserStatus} status={userOrganization?.is_active} loading={statusLoading} />
        </>
    );
};

export default UserDetails;
